/**
 * Every UI and email test, written in code. The dash runs what is here and
 * cannot invent a test; a test here that nobody started serves control.
 *
 * Adding one: an entry below, then wrap ONLY the part that changes in
 * `<UiTest test="..." arms={{ control: () => ..., other: () => ... }}>` (or
 * `useUiTestArm` for a value). The arm keys below are the type the wrapper
 * checks, so a missing or misspelled arm does not compile. Plan and rules:
 * docs/UI_TESTS_PLAN.md.
 */
import type { UiTestGoal } from '../../schemas/ui-tests';

export interface UiTestDefinition {
  /** What a person calls it on the dash. */
  label: string;
  surface: 'web' | 'email';
  /** An email test's Mandrill template. One running test per template, so a click names its test. */
  template?: string;
  /** What should happen, and what would make it a win. */
  hypothesis: string;
  /** Arm id -> what it shows. `control` is required and is always what ships today. */
  arms: { readonly control: string } & Readonly<Record<string, string>>;
  /** Who is counted. Absent means everyone who sees the wrapped part. */
  eligible?: string;
  goals: { primary: UiTestGoal; also: readonly UiTestGoal[] };
}

/** Keeps the arm keys as literal types, so `UiArmOf` can check them. */
function defineUiTests<const T extends Record<string, UiTestDefinition>>(tests: T): T {
  return tests;
}

export const UI_TESTS = defineUiTests({
  'home-default-view': {
    label: 'Home starts on Showcase or Grid',
    surface: 'web',
    hypothesis: 'Starting on the showcase rows gets more listing views per visit than starting on the grid.',
    arms: {
      control: 'Grid (today)',
      showcase: 'Showcase rows',
    },
    eligible: 'Visitors who have never picked a view with the toggle',
    goals: { primary: 'viewsPerSession', also: ['clickThrough', 'checkoutDepth', 'returnRate'] },
  },
  'thumb-location': {
    label: 'Location on listing thumbs',
    surface: 'web',
    hypothesis: 'Showing where an item is, where the Verified badge sits on the price row, gets more clicks on thumbs.',
    arms: {
      control: 'Verified badge on the price row (today)',
      location: 'Town on the price row, plain text',
    },
    goals: { primary: 'clickThrough', also: ['viewsPerSession', 'checkoutDepth'] },
  },
  'vendoo-welcome-subject': {
    label: 'Vendoo welcome: their name in the subject',
    surface: 'email',
    template: 'vendoo-seller-welcome',
    hypothesis: 'Starting the subject with the seller\'s first name gets more of them to open the welcome and click through to their shop or sign in.',
    arms: {
      control: 'Your Vendoo listings are on Sellwild (today)',
      named: '<First name>, your Vendoo listings are live on Sellwild',
    },
    eligible: 'Sellers Vendoo registers with us who gave a first name, split by email address',
    goals: { primary: 'emailClickRate', also: ['returnRate', 'viewsPerSession'] },
  },
});

export type UiTestId = keyof typeof UI_TESTS & string;
export type UiArmOf<T extends UiTestId> = keyof (typeof UI_TESTS)[T]['arms'] & string;

export const UI_TEST_IDS = Object.keys(UI_TESTS) as UiTestId[];

export function isUiTestId(id: string): id is UiTestId {
  return Object.prototype.hasOwnProperty.call(UI_TESTS, id);
}

/** The email test on a Mandrill template, or null. */
export function emailTestForTemplate(template: string): UiTestId | null {
  const def = (id: UiTestId): UiTestDefinition => UI_TESTS[id];
  return UI_TEST_IDS.find((id) => def(id).surface === 'email' && def(id).template === template) ?? null;
}

/** The arm ids of a test, control first, in the order written. */
export function armsOf(test: UiTestId): string[] {
  const ids = Object.keys(UI_TESTS[test].arms);
  return ['control', ...ids.filter((id) => id !== 'control')];
}
